/* topbar-inova.js — the top bar marquee, copied from inova's `MarqueeElement` + `ControlButton` + the StickyHeader rule that squares
 * the header's top corners (source bytes: theme/design/regimes/out/review-stli-14.9/source-js/bytes-20.9/theme.js).
 * Theme 97.0, 24.9.2026.
 *
 *  - no start at all under prefers-reduced-motion (the source's `!theme.config.motionReduced`)
 *  - start: in view with a 200px margin (mouse), or on the first interaction / after 5 s (touch, `theme.initWhenVisible`)
 *  - clones: ceil(parent / child) + 2, each aria-hidden, links and buttons tabindex -1
 *  - --tbar-duration = (33 - speed) * min(2.5, ceil(child / parent)) seconds
 *  - paused while out of view (IntersectionObserver, rootMargin 0 0 50px 0)
 *  - the header's corners: square when scrollTop > header top + header height + 10, round again below that − 10
 * Deviation (recorded in the map): the source's pause button only swaps its label; here it really stops the marquee.
 */
(function () {
  if (window.__tbarInova) return;
  window.__tbarInova = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function width(el) {
    var r = el.getBoundingClientRect();
    return r.right - r.left;
  }

  function whenVisibleTouch(cb) {
    var done = false;
    var events = ['click', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'wheel'];
    function fire() {
      if (done) return;
      done = true;
      events.forEach(function (t) { window.removeEventListener(t, fire); });
      clearTimeout(timer);
      cb();
    }
    events.forEach(function (t) { window.addEventListener(t, fire, { passive: true }); });
    var timer = setTimeout(fire, 5000);
  }

  function initMarquee(scroller) {
    if (scroller.dataset.tbarInit) return;
    scroller.dataset.tbarInit = '1';
    var first = scroller.firstElementChild;
    if (!first || scroller.childElementCount !== 1) return;
    // 250.0 candidate 5 (3.11.2026, Head 118.0): ONE animated track, static clones inside. Every main frame restyles every running animation
    // (inval_dump / blank_anim: a rAF loop from any app script + 7 animated clones = 7 restyles per frame; 1 track = 1). The track moves exactly
    // one clone width per --tbar-duration, the same distance and direction each clone moved (-100% -> -200% of its own width): same speed, no seam.
    var parentW = width(scroller);
    var childW = width(first);
    var max = childW > 0 ? Math.ceil(parentW / childW) + 2 : 0;
    var track = document.createElement('div');
    track.className = 'tbar__track';
    track.appendChild(first);
    for (var i = 0; i < max; i++) {
      var clone = first.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('button, [href]').forEach(function (el) { el.setAttribute('tabindex', '-1'); });
      track.appendChild(clone);
    }
    track.style.setProperty('--tbar-clones', String(max + 1));
    scroller.appendChild(track);
    track.classList.add('is-animated');
    var speed = parseInt(scroller.getAttribute('data-speed') || '16', 10);
    scroller.style.setProperty('--tbar-duration', (33 - speed) * Math.min(2.5, Math.ceil(childW / parentW)) + 's');
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { scroller.classList.toggle('is-paused', !e.isIntersecting); });
    }, { rootMargin: '0px 0px 50px 0px' }).observe(scroller);
  }

  function setupMarquee(scroller) {
    if (reduced.matches) return;
    if (!isTouch || designMode) {
      var io = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
          io.disconnect();
          initMarquee(scroller);
        }
      }, { rootMargin: '200px 0px 200px 0px' });
      io.observe(scroller);
    } else {
      whenVisibleTouch(function () { initMarquee(scroller); });
    }
  }

  function setupControl(button) {
    var scroller = document.getElementById(button.getAttribute('aria-controls'));
    button.addEventListener('click', function () {
      var paused = button.getAttribute('aria-pressed') === 'true';
      button.setAttribute('aria-pressed', paused ? 'false' : 'true');
      button.setAttribute('aria-label', paused ? button.dataset.pauseLabel : button.dataset.playLabel);
      if (scroller) scroller.classList.toggle('is-stopped', !paused);
    });
  }

  // The header's corners: the source's StickyHeader threshold, read once at start like the source (firstScrollTop + bounds).
  function setupHeader(section) {
    var header = section.nextElementSibling;
    if (!header || !header.classList.contains('header-section')) return;
    var wrapper = document.querySelector('.page-wrapper');
    function scrollerOf() { return wrapper && wrapper.scrollHeight > wrapper.clientHeight && getComputedStyle(wrapper).overflowY !== 'visible' ? wrapper : null; }
    function top() { var sc = scrollerOf(); return Math.max(sc ? sc.scrollTop : 0, window.scrollY || 0); }
    var first = top();
    var threshold = header.getBoundingClientRect().top + first + header.getBoundingClientRect().height;
    var scrolled = false;
    // 252.0 candidate 10b (3.11.2026), built on candidate 5 (3851704cf): ZERO reads per scroll frame. The old frame read scrollHeight /
    // clientHeight / getComputedStyle / scrollTop on every notch and paid a forced layout each time (251 Report 4; sweeps c10 / c10b: caching
    // the scroller choice, or reading in the scroll event, only moved the flush to the next reader - with an animation running Blink dirties
    // style every frame and the FIRST layout read pays, whoever it is). Instead the topbar section itself - in flow, right above the sticky
    // header - is watched by two IntersectionObservers on the page's scroll container (the root must be the scroller: a margin on the
    // implicit root is clipped at its edge on desktop, theme/LEARNED.md 3.11 252.0). The threshold is the same expression as before
    // (rect px + scrollTop, as the source's StickyHeader; at 1496 that is 106.6 and the class flips ON past 117 / OFF under 97 - cand10_probe),
    // turned into the two margins: the section's bottom (layout px) is (threshold +- 10) above the root's top edge. Same hysteresis, same
    // positions, no read per frame. The scroll path below stays only without IntersectionObserver or when the topbar has no box.
    var zoom = section.currentCSSZoom || 1;
    var tbarBottom = section.getBoundingClientRect().bottom / zoom + first;
    var marginOn = Math.round(threshold + 10 - tbarBottom), marginOff = Math.round(threshold - 10 - tbarBottom);
    header._tbarDbg = { first: first, threshold: threshold, tbarBottom: tbarBottom, marginOn: marginOn, marginOff: marginOff, zoom: zoom, at: document.readyState }; // read by review-252 probes
    if ('IntersectionObserver' in window && section.getBoundingClientRect().height > 0 && getComputedStyle(section).position !== 'sticky' && getComputedStyle(section).position !== 'fixed') {
      var root = scrollerOf();
      new IntersectionObserver(function (es) { if (!es[es.length - 1].isIntersecting && !scrolled) { scrolled = true; header.classList.add('tbar-header-scrolled'); } }, { root: root, rootMargin: marginOn + 'px 0px 0px 0px' }).observe(section);
      new IntersectionObserver(function (es) { if (es[es.length - 1].isIntersecting && scrolled) { scrolled = false; header.classList.remove('tbar-header-scrolled'); } }, { root: root, rootMargin: marginOff + 'px 0px 0px 0px' }).observe(section);
      return;
    }
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var t = top();
        if (!scrolled && t > threshold + 10) {
          scrolled = true;
          header.classList.add('tbar-header-scrolled');
        } else if (scrolled && t < threshold - 10) {
          scrolled = false;
          header.classList.remove('tbar-header-scrolled');
        }
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function init(root) {
    root.querySelectorAll('.tbar__scroller').forEach(setupMarquee);
    root.querySelectorAll('.tbar__control').forEach(setupControl);
    root.querySelectorAll('.tbar-section').forEach(setupHeader);
    if (root.classList && root.classList.contains('tbar-section')) setupHeader(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);

  document.addEventListener('shopify:section:load', function (e) {
    window.__tbarInova = true;
    init(e.target);
  });
})();
